import React, { useState } from "react";
import { Modal, Box, Typography, TextField, Button } from "@mui/material";
import { addPartner } from "../../services/partnerInformation";
import { newPartnerInfo, PartnerInfo } from "../../data/partnerData";
import "../admin/Admin.css";

interface AddPartnerModalProps {
    isOpen: boolean;
    onClose: () => void;
    setSnackbarMessage: (message: string) => void;
    setSnackbarOpen: (open: boolean) => void;
    refreshPartners: () => void;
}

// Styling similar to the Edit Modal
const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const AddPartnerModal: React.FC<AddPartnerModalProps> = ({isOpen, onClose, setSnackbarOpen, setSnackbarMessage, refreshPartners}) => {
    const [partner, setPartner] = useState<PartnerInfo>(newPartnerInfo());
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Validation states
    const [errors, setErrors] = useState({
        ContactName: "",
        EmailAddress: "",
        ContactPhone: "",
        Name: "",
        Description: "",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setPartner({ ...partner, [e.target.name]: e.target.value });

        // Clear validation error when user types
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const validateFields = () => {
        let newErrors = { ...errors };
        let isValid = true;

        if (!partner.ContactName.trim()) {
            newErrors.ContactName = "Contact name is required.";
            isValid = false;
        }
        if (!partner.EmailAddress.trim()) {
            newErrors.EmailAddress = "Email is required.";
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partner.EmailAddress)) {
            newErrors.EmailAddress = "Invalid email format.";
            isValid = false;
        }
        if (!partner.ContactPhone.trim()) {
            newErrors.ContactPhone = "Phone number is required.";
            isValid = false;
        } else if (!/^\d+$/.test(partner.ContactPhone)) {
            newErrors.ContactPhone = "Phone number must contain only numbers.";
            isValid = false;
        }
        if (!partner.Name.trim()) {
            newErrors.Name = "Company name is required.";
            isValid = false;
        }
        if (!partner.Description.trim()) {
            newErrors.Description = "Description is required.";
            isValid = false;
        } else if (partner.Description && partner.Description.length > 500) {
            newErrors.Description = "Description must be under 500 characters.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateFields()) return; // Prevent submission if validation fails

        setLoading(true);
        setErrorMessage(null);

        try {
            const response = await addPartner(partner); // Call the API service function
            console.log("Partner added successfully:", response);

            // Show success message
            setSnackbarMessage("Partner added successfully");
            setSnackbarOpen(true);

            refreshPartners();

            onClose(); // Close modal after snackbar trigger
        } catch (error) {
            console.error("Submit Error:", error);
            setErrorMessage("Failed to add partner. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="add-partner-modal">
            <Box sx={modalStyle}>
                <Typography variant="h6" component="h2">
                    Add A New Partner
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Contact Name"
                        name="ContactName"
                        value={partner.ContactName}
                        onChange={handleChange}
                        error={Boolean(errors.ContactName)}
                        helperText={errors.ContactName}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Email Address"
                        name="EmailAddress"
                        type="email"
                        value={partner.EmailAddress}
                        onChange={handleChange}
                        error={Boolean(errors.EmailAddress)}
                        helperText={errors.EmailAddress}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Phone Number"
                        name="ContactPhone"
                        value={partner.ContactPhone}
                        onChange={handleChange}
                        error={Boolean(errors.ContactPhone)}
                        helperText={errors.ContactPhone}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Company Name"
                        name="Name"
                        value={partner.Name}
                        onChange={handleChange}
                        error={Boolean(errors.Name)}
                        helperText={errors.Name}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Description"
                        name="Description"
                        multiline
                        rows={3}
                        value={partner.Description}
                        onChange={handleChange}
                        error={Boolean(errors.Description)}
                        helperText={errors.Description}
                    />
                    {errorMessage && <Typography color="error">{errorMessage}</Typography>}
                </Box>
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={onClose} sx={{ mr: 1 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" className="editOrAdd" disabled={loading}>
                        {loading ? "Submitting..." : "Submit"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddPartnerModal;
